<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

// vite-svg-loader's svgo strips viewBox, so the usage sets it explicitly — without it,
// sizing the icon below its intrinsic 16px via CSS clips the artwork instead of scaling.
import CheckIcon from '@/assets/svg/check-16.svg?component';

const props = withDefaults(
  defineProps<{
    // Dwell time (ms) before auto-advancing each of the first two stages (matches the
    // prototype's 500ms → 1050ms cadence). The final stage holds until the parent unmounts
    // the loader (results arrived). Overridable so tests can drive it with fake timers.
    stageDurationsMs?: [number, number];
  }>(),
  {
    stageDurationsMs: () => [500, 550],
  },
);

// Natural-language feedback states (ONE-6171 / TDD §2.6). The loader is purely visual: it
// advances on a fixed timeline and never reflects real backend progress — the NL
// round-trip is a single request, so there are no progress events to follow.
const stageIndex = ref(0);
let timers: ReturnType<typeof setTimeout>[] = [];

const stages = ['Understanding your query', 'Searching across candidates', 'Ranking by fit'];

onMounted(() => {
  const [first, second] = props.stageDurationsMs;
  timers.push(setTimeout(() => (stageIndex.value = 1), first));
  timers.push(setTimeout(() => (stageIndex.value = 2), first + second));
});

onBeforeUnmount(() => {
  timers.forEach(clearTimeout);
  timers = [];
});
</script>

<template>
  <div class="staged-loader" role="status" aria-live="polite" data-test="staged-search-loader">
    <div class="stages">
      <div
        v-for="(label, index) in stages"
        :key="index"
        class="stage"
        :class="{ 'stage--done': index < stageIndex, 'stage--active': index === stageIndex }"
        :data-test="`staged-search-loader-stage-${index}`"
      >
        <span class="stage-marker">
          <CheckIcon v-if="index < stageIndex" class="stage-check" viewBox="0 0 16 16" />
          <span v-else-if="index === stageIndex" class="stage-spinner" />
          <span v-else class="stage-dot" />
        </span>
        <span class="stage-label">{{ label }}…</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* Mirrors the prototype's loading state (project-26-c3 smart-input design): a centered
   column of left-aligned steps, dimmed until reached, current in dark bold, done in green.
   Colors are the design's literal tokens (no exact Tailwind shade match for the light ring
   / pending fill, so they're inlined). */
.staged-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 80px 0 60px;
}
.stages {
  display: flex;
  min-width: 280px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}
.stage {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #94a3b8; /* b400 — pending */
  opacity: 0.5;
  transition:
    color 260ms ease,
    opacity 260ms ease;
}
.stage--active {
  color: #0f172a; /* b880 — current */
  font-weight: 600;
  opacity: 1;
}
.stage--done {
  color: #159b54; /* FF success — completed (check inherits via currentColor) */
  opacity: 1;
}
.stage-marker {
  display: inline-flex;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
}
.stage-check {
  width: 14px;
  height: 14px;
}
.stage-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #e2e8f0; /* b80 ring */
  border-top-color: #159b54; /* FF green */
  animation: staged-spin 700ms linear infinite;
}
@keyframes staged-spin {
  to {
    transform: rotate(360deg);
  }
}
.stage-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #cbd5e1; /* b100 */
}
</style>
